import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { MoreHorizontal, ChevronDown } from "lucide-react";

/**
 * Compact "More ▾" menu for secondary nav.
 *
 * The dashboard header was wrapping to two rows on 1366px advisor monitors
 * — there were 10 buttons in a single flex row. We keep the high-frequency
 * actions (Accounts, Group, Settings, Team, ThemeToggle, Sign out) inline
 * and tuck the low-frequency ones (How it works, Catalog, Reset demo) in
 * here so the row never wraps.
 *
 * Items prop:
 *   - Each entry: { id, label, icon: LucideIcon, to?: string, onClick?, tone? }
 *   - `tone` accepts "warning" / "primary" so e.g. Reset demo renders in the
 *     warning palette inside the menu.
 *   - Falsy entries are skipped so callers can do conditional inclusion
 *     inline (e.g. `isDemoUser && {id:"reset",…}`).
 */
export default function HeaderMoreMenu({ items = [] }) {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);

  // Close on outside click + escape — same UX as ThemeToggle for consistency.
  useEffect(() => {
    if (!open) return;
    const onClick = (e) => {
      if (!wrapRef.current?.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
    window.addEventListener("click", onClick);
    window.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener("click", onClick);
      window.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const visible = items.filter(Boolean);
  if (visible.length === 0) return null;

  return (
    <div ref={wrapRef} className="relative" data-testid="header-more-menu">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="btn-outline"
        title="More options"
        data-testid="header-more-btn"
      >
        <MoreHorizontal className="w-4 h-4" />
        <span className="hidden lg:inline">More</span>
        <ChevronDown className={`w-3 h-3 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>

      {open && (
        <div
          className="absolute right-0 mt-2 w-60 rounded-lg border border-border bg-card shadow-2xl overflow-hidden z-50 animate-fade-up"
          role="menu"
          data-testid="header-more-dropdown"
        >
          {visible.map((it) => {
            const Icon = it.icon;
            const toneCls =
              it.tone === "warning"
                ? "text-warning hover:bg-warning/10"
                : it.tone === "primary"
                  ? "text-primary hover:bg-primary/10"
                  : "text-foreground hover:bg-muted";
            const inner = (
              <>
                {Icon && <Icon className="w-4 h-4 shrink-0" />}
                <span className="flex-1 text-left">{it.label}</span>
              </>
            );
            const cls = `w-full px-4 py-2.5 text-sm flex items-center gap-3 transition-colors ${toneCls}`;
            // Either a Link, an onClick button, or both (onClick fires before
            // routing — useful for analytics tagging later if we want it).
            if (it.to) {
              return (
                <Link
                  key={it.id}
                  to={it.to}
                  onClick={() => { setOpen(false); it.onClick?.(); }}
                  className={cls}
                  data-testid={`header-more-${it.id}`}
                >
                  {inner}
                </Link>
              );
            }
            return (
              <button
                key={it.id}
                onClick={() => { setOpen(false); it.onClick?.(); }}
                className={cls}
                data-testid={`header-more-${it.id}`}
              >
                {inner}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
